import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export const LOCALES = ['ar', 'fr'];
export const DEFAULT_LOCALE = 'ar';
export const AR_BASE = '/ar';
export const FR_BASE = '/fr';

const APP_PREFIXES = ['/admin', '/login', '/operateur', '/maitresse', '/donateur', '/orphelin'];

export const isLocale = (value) => value === 'ar' || value === 'fr';

export const localeBase = (locale) => (locale === 'fr' ? FR_BASE : AR_BASE);

export const getLocaleFromPath = (pathname = '') => {
  const match = String(pathname).match(/^\/(ar|fr)(?=\/|$)/);
  return match ? match[1] : DEFAULT_LOCALE;
};

export const stripLocalePrefix = (pathname = '') => {
  const stripped = String(pathname).replace(/^\/(ar|fr)(?=\/|$)/, '');
  return stripped || '/';
};

const splitPathSuffix = (value) => {
  const hashIndex = value.indexOf('#');
  const queryIndex = value.indexOf('?');
  const cut = [hashIndex, queryIndex].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (cut === undefined) return { path: value, suffix: '' };
  return { path: value.slice(0, cut), suffix: value.slice(cut) };
};

/** Construit un chemin public sous /ar ou /fr */
export const localePath = (subPath = '', locale = DEFAULT_LOCALE) => {
  const lng = isLocale(locale) ? locale : DEFAULT_LOCALE;
  const base = localeBase(lng);
  if (!subPath || subPath === '/') return base;
  if (/^(https?:|mailto:|tel:)/i.test(subPath)) return subPath;

  const { path, suffix } = splitPathSuffix(subPath);
  let normalized = path.startsWith('/') ? path : `/${path}`;
  if (APP_PREFIXES.some((p) => normalized === p || normalized.startsWith(`${p}/`))) {
    return `${normalized}${suffix}`;
  }
  normalized = stripLocalePrefix(normalized);
  if (normalized === '/') return `${base}${suffix}`;
  return `${base}${normalized}${suffix}`;
};

export const switchLocalePath = (fullPath, nextLocale) => localePath(fullPath || '/', nextLocale);

/** @deprecated Prefer localePath(subPath, locale) */
export const arPath = (subPath = '') => localePath(subPath, 'ar');

export const getPreferredLocale = () => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = String(window.localStorage.getItem('i18nextLng') || '').toLowerCase();
    if (stored.startsWith('fr')) return 'fr';
    if (stored.startsWith('ar')) return 'ar';
  } catch {
    // ignore
  }
  return DEFAULT_LOCALE;
};

export const createPublicPaths = (locale = DEFAULT_LOCALE) => {
  const p = (sub) => localePath(sub, locale);
  return {
    home: p('/'),
    programmes: p('/programmes'),
    programme: (slug) => p(`/programmes/${slug}`),
    about: p('/about'),
    stats: p('/stats'),
    dossiers: p('/dossiers'),
    projet: (slug) => p(`/projets/${slug}`),
    donate: p('/donate'),
    volunteer: p('/volunteer'),
    actualites: p('/actualites'),
    actualite: (id) => p(`/actualites/${id}`),
    legal: p('/legal'),
    kafalaOrphelin: (id) => p(`/programmes/kafala/orphelins/${id}`),
  };
};

export const PUBLIC_PATHS = createPublicPaths(DEFAULT_LOCALE);

export const normalizePublicLink = (link, locale = DEFAULT_LOCALE) => {
  if (!link || typeof link !== 'string') return localePath('/', locale);
  return localePath(link, locale);
};

export const usePublicLocale = () => {
  const { pathname } = useLocation();
  return getLocaleFromPath(pathname);
};

export const usePublicPaths = () => {
  const locale = usePublicLocale();
  return useMemo(() => createPublicPaths(locale), [locale]);
};

export const dateLocaleOf = (locale) => (locale === 'fr' ? 'fr-FR' : 'ar-MA');
